import { ptPT } from './ptPT';
import { frFR } from './frFR';

export interface LanguagePack {
    errors: Record<string, string>;
    functions: Record<string, string>;
    langCode: string;
}

const languagePacks: Record<string, LanguagePack> = {
    pt: ptPT,
    fr: frFR,
};

// Build reverse maps (localized name → english key) for function resolution
// Case-insensitive: all keys are uppercased
const reverseMaps: Record<string, Record<string, string>> = {};

for (const [lang, pack] of Object.entries(languagePacks)) {
    const reverseMap: Record<string, string> = {};
    for (const [englishKey, localizedName] of Object.entries(pack.functions)) {
        if (localizedName && localizedName.trim() !== '') {
            reverseMap[localizedName.toUpperCase()] = englishKey;
        }
    }
    reverseMaps[lang] = reverseMap;
}

/**
 * Get the language pack for a given language code.
 * Returns the pack in the same format as HyperFormula's language packs.
 */
export function getLanguagePack(lang: string): LanguagePack {
    return languagePacks[lang] ?? languagePacks.pt;
}

/**
 * Get all registered language packs.
 */
export function getAllLanguagePacks(): Record<string, LanguagePack> {
    return languagePacks;
}

/**
 * Get the forward mapping (english key → localized name) for a language.
 */
export function getFunctionMapping(lang: string): Record<string, string> {
    const pack = languagePacks[lang] ?? languagePacks.pt;
    return pack.functions;
}

/**
 * Get the reverse mapping (localized name → english key) for a language.
 * All keys are uppercased for case-insensitive lookup.
 */
export function getReverseFunctionMapping(lang: string): Record<string, string> {
    return reverseMaps[lang] ?? reverseMaps.pt;
}

/**
 * Get a unified reverse mapping combining ALL languages.
 * Maps any localized function name (uppercased) → english key.
 * Useful for translating formulas stored in any language.
 */
const _unifiedReverseMap: Record<string, string> = {};
for (const reverseMap of Object.values(reverseMaps)) {
    for (const [localizedName, englishKey] of Object.entries(reverseMap)) {
        _unifiedReverseMap[localizedName] = englishKey;
    }
}
// Also add English keys mapping to themselves (identity)
for (const pack of Object.values(languagePacks)) {
    for (const englishKey of Object.keys(pack.functions)) {
        if (englishKey && englishKey.trim() !== '') {
            _unifiedReverseMap[englishKey.toUpperCase()] = englishKey;
        }
    }
}

export function getUnifiedReverseFunctionMapping(): Record<string, string> {
    return _unifiedReverseMap;
}

export { ptPT, frFR };
